import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Right aligned side panel rather than a centred dialog. */
  side?: boolean;
}

/** Accessible modal: focus moves in on open, Escape closes, focus returns on close. */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  side = false
}: DialogProps) {
  const reduce = useReducedMotion();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  const descId = React.useId();
  React.useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => panelRef.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus(), 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previous?.focus();
    };
  }, [open, onClose]);
  const enter = side ? {
    x: reduce ? 0 : 24,
    opacity: 0
  } : {
    scale: reduce ? 1 : 0.96,
    opacity: 0
  };
  return <AnimatePresence data-id="element-965" data-mp="1tl9nfsed8gv0">
      {open && <div className={`fixed inset-0 z-50 flex ${side ? 'justify-end' : 'items-end justify-center p-0 sm:items-center sm:p-6'}`} data-id="element-966" data-mp="20ozoy16wvriz">
          <motion.div className="absolute inset-0 bg-[#020B18]/70" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18
      }} onClick={onClose} aria-hidden="true" data-id="element-967" data-mp="25n9jc9ktao3j" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descId : undefined} initial={enter} animate={{
        x: 0,
        scale: 1,
        opacity: 1
      }} exit={enter} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className={`relative flex max-h-full w-full flex-col overflow-hidden border border-line bg-panel shadow-2xl ${side ? 'h-full max-w-lg border-y-0 border-r-0' : 'max-h-[92vh] rounded-t-lg sm:max-w-lg sm:rounded-lg'}`} data-id="element-968" data-mp="1hzjae9ye5yjk">
            <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-5" data-id="element-969" data-mp="3hf0jmzcn7tkx">
              <div data-id="element-970" data-mp="3ddx12537njg0">
                <h2 id={titleId} className="font-display text-[20px] text-ink" data-id="element-971" data-mp="38g5dfn9lmjhm">
                  {title}
                </h2>
                {description && <p id={descId} className="mt-1 text-[14px] text-muted" data-id="element-972" data-mp="33jocttl7qcnn">
                    {description}
                  </p>}
              </div>
              <button type="button" onClick={onClose} className="-mr-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm text-muted transition-colors duration-150 hover:bg-charcoal hover:text-ink" aria-label="Close" data-id="element-973" data-mp="2tqz599ss9ixl">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-974" data-mp="2fr7vjl9auope" />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-6" data-id="element-975" data-mp="2atogwk8ow9wx">{children}</div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}